// src/AdminAuthorRow.tsx
// One account's row in the admin "All authors" list — identity/application
// summary, per-account settings (AI access, storage quota, daily render and
// AI description limits), plus whatever
// status actions are legal from its current status. Owns its own busy/
// note-box state, so multiple rows never interfere with each other.
import { useState } from 'react';
import { runAuthorAction, updateAuthorSettings, type AdminAuthor } from './adminApi';
import {
  actionsAvailableFrom, isValidDailyLimit, MAX_DAILY_LIMIT, MAX_STORAGE_QUOTA_MB, type AdminAuthorAction,
} from '@ring/shared/author-trust';

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function AdminAuthorRow({ author, onUpdated }: {
  author:    AdminAuthor;
  onUpdated: (updated: AdminAuthor) => void;
}) {
  const [busy, setBusy]                   = useState(false);
  const [pendingAction, setPendingAction] = useState<AdminAuthorAction | null>(null);
  const [noteText, setNoteText]           = useState('');
  const [quotaText, setQuotaText]         = useState(String(author.storageQuotaMb));
  const [rendersText, setRendersText]     = useState(String(author.rendersPerDay));
  const [aiImagesText, setAiImagesText]   = useState(String(author.aiImagesPerDay));
  const [error, setError]                 = useState<string | null>(null);

  // Shared by every action below — one `busy` flag for the whole row so two
  // admin actions on the same row can never run concurrently and race to
  // overwrite each other's onUpdated snapshot.
  async function runAction(action: () => Promise<AdminAuthor>) {
    setError(null);
    setBusy(true);
    try {
      onUpdated(await action());
      setPendingAction(null);
      setNoteText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  }

  // Derived from the shared AUTHOR_ACTIONS table (@ring/shared/author-trust) — the
  // same table adminAuthors.ts's route handlers gate against, so which buttons show
  // here can't drift out of sync with which transitions the backend actually allows.
  const actions = actionsAvailableFrom(author.authorStatus);
  const pendingRule = pendingAction ? actions.find(([action]) => action === pendingAction)?.[1] : undefined;

  const quota = Number(quotaText);
  const quotaValid = Number.isInteger(quota) && quota >= 1 && quota <= MAX_STORAGE_QUOTA_MB;
  const rendersPerDay = Number(rendersText);
  const aiImagesPerDay = Number(aiImagesText);
  const limitsValid = rendersText !== '' && aiImagesText !== '' && isValidDailyLimit(rendersPerDay) && isValidDailyLimit(aiImagesPerDay);
  const limitsChanged = rendersPerDay !== author.rendersPerDay || aiImagesPerDay !== author.aiImagesPerDay;

  return (
    <li className="admin-row admin-row-author">
      <div className="admin-row-top">
        <div className="admin-row-main">
          <span className="admin-row-title">{author.email}</span>
          {author.isAdmin && <span className="admin-badge admin-badge-admin">Admin</span>}
          <span className={`admin-badge admin-badge-status-${author.authorStatus}`}>{author.authorStatus}</span>
        </div>
        <div className="admin-row-dates">
          <span>Joined {formatDate(author.createdAt)}</span>
          <span>Last login {formatDate(author.lastLogin)}</span>
        </div>
      </div>
      {author.statusNote && (
        <div className="admin-row-application">
          <span className="admin-row-note">Note: {author.statusNote}</span>
        </div>
      )}

      <div className="admin-row-application">
        <span>AI jobs: {author.aiEnabled ? 'on' : 'off'}</span>
        <button disabled={busy} onClick={() => runAction(() => updateAuthorSettings(author.userId, { aiEnabled: !author.aiEnabled }))}>
          {author.aiEnabled ? 'Turn AI off' : 'Turn AI on'}
        </button>
        <label className="admin-row-quota">
          Storage (MB)
          <input type="number" min={1} max={MAX_STORAGE_QUOTA_MB} value={quotaText} onChange={e => setQuotaText(e.target.value)} />
        </label>
        <button
          disabled={busy || !quotaValid || quota === author.storageQuotaMb}
          onClick={() => runAction(() => updateAuthorSettings(author.userId, { storageQuotaMb: quota }))}
        >
          Save quota
        </button>
      </div>

      {/* Counted over the last 24 hours. 0 stops that kind of job for the account. */}
      <div className="admin-row-application">
        <label className="admin-row-quota">
          Renders a day
          <input type="number" min={0} max={MAX_DAILY_LIMIT} value={rendersText} onChange={e => setRendersText(e.target.value)} />
        </label>
        <label className="admin-row-quota">
          AI descriptions a day
          <input type="number" min={0} max={MAX_DAILY_LIMIT} value={aiImagesText} onChange={e => setAiImagesText(e.target.value)} />
        </label>
        <button
          disabled={busy || !limitsValid || !limitsChanged}
          onClick={() => runAction(() => updateAuthorSettings(author.userId, { rendersPerDay, aiImagesPerDay }))}
        >
          Save limits
        </button>
      </div>

      {error && <p className="page-error-text">{error}</p>}

      {pendingAction ? (
        <div className="admin-note-row">
          <input
            type="text"
            placeholder="Reason (required)"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            autoFocus
          />
          <button
            disabled={busy || !noteText.trim()}
            onClick={() => runAction(() => runAuthorAction(author.userId, pendingAction, noteText.trim()))}
          >
            {pendingRule?.label ?? 'Confirm'}
          </button>
          <button onClick={() => { setPendingAction(null); setNoteText(''); }}>Cancel</button>
        </div>
      ) : actions.length > 0 && (
        <div className="admin-actions">
          {actions.map(([action, rule]) => (
            <button
              key={action}
              disabled={busy}
              onClick={() => {
                if (rule.requiresNote) {
                  setNoteText('');
                  setPendingAction(action);
                } else {
                  runAction(() => runAuthorAction(author.userId, action));
                }
              }}
            >
              {rule.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
